import { useMemo, useState } from 'react';
import {
  Search,
  BarChart3,
  TrendingUp,
  Wallet,
  Clock,
  AlertTriangle,
  Sparkles,
  ArrowDownAZ,
  Scale,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { usePayroll } from '@/context/AppContext';
import { calcStats, groupByJob, fmtNum, fmtInt } from '@/lib/payroll';

export function ResultsPage() {
  const { payrollData } = usePayroll();
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState<'net' | 'name' | 'days' | 'late'>('name');

  const stats = useMemo(() => calcStats(payrollData), [payrollData]);
  const byJob = useMemo(() => (payrollData ? groupByJob(payrollData) : {}), [payrollData]);

  // ✅ جدول الموظفين: ترتيب أبجدي افتراضي بالاسم
  const rows = useMemo(() => {
    if (!payrollData) return [];
    let r = [...payrollData];
    if (filter.trim()) {
      const q = filter.trim().toLowerCase();
      r = r.filter(
        (d) =>
          d.Name?.toLowerCase().includes(q) ||
          String(d.ID).includes(q) ||
          d.Job?.toLowerCase().includes(q),
      );
    }
    if (sortBy === 'net') r.sort((a, b) => b['صافي القبض المستحق'] - a['صافي القبض المستحق']);
    else if (sortBy === 'name') r.sort((a, b) => String(a.Name).localeCompare(String(b.Name), 'ar'));
    else if (sortBy === 'days') r.sort((a, b) => b['أيام الحضور المستحقة'] - a['أيام الحضور المستحقة']);
    else if (sortBy === 'late')
      r.sort((a, b) => (b['دقائق التأخير'] || 0) - (a['دقائق التأخير'] || 0));
    return r;
  }, [payrollData, filter, sortBy]);

  // ✅ جدول الوظائف: ترتيب أبجدي دائماً باسم الوظيفة
  const sortedByJob = useMemo(() => {
    return Object.entries(byJob).sort((a, b) =>
      String(a[0]).localeCompare(String(b[0]), 'ar'),
    );
  }, [byJob]);

  if (!payrollData || !stats) {
    return (
      <div className="space-y-5 animate-slide-up">
        <h2 className="text-2xl font-extrabold flex items-center gap-2">
          <BarChart3 size={22} className="text-violet-500" />
          النتائج والإحصائيات
        </h2>
        <Card className="text-center !p-12">
          <p className="text-slate-600 dark:text-slate-300">احسب المرتبات أولاً لعرض النتائج</p>
        </Card>
      </div>
    );
  }

  const topJobs = sortedByJob;

  return (
    <div className="space-y-5 animate-slide-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-extrabold flex items-center gap-2">
          <BarChart3 size={22} className="text-violet-500" />
          النتائج والإحصائيات
        </h2>
        <Badge variant="emerald">{payrollData.length} موظف</Badge>
      </div>

      {/* Top stat cards — الإجمالي الأصلي + الإجمالي بعد التعديل الإداري */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={Scale}
          label="الإجمالي الأصلي"
          value={fmtNum(stats.totalNetBeforeAdmin)}
          color="from-slate-500 to-slate-700"
          sub="بدون التعديلات الإدارية"
        />
        <StatCard
          icon={Wallet}
          label="الإجمالي بعد التعديل"
          value={fmtNum(stats.totalNet)}
          color="from-emerald-500 to-teal-500"
          sub="مع الزيادات/الخصومات الإدارية"
        />
        <StatCard
          icon={TrendingUp}
          label="إجمالي الراتب"
          value={fmtNum(stats.totalGross)}
          color="from-indigo-500 to-violet-500"
          sub="ر.أساسي + إضافي + تعديلات"
        />
        <StatCard
          icon={AlertTriangle}
          label="إجمالي الخصومات"
          value={fmtNum(stats.totalDed)}
          color="from-rose-500 to-pink-500"
          sub="تأخير + خروج مبكر + نسيان"
        />
      </div>

      {/* Admin adjustments row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniStat label="إجمالي أيام التعديل الإداري" value={fmtNum(stats.adjDays)} color="sky" icon={Sparkles} />
        <MiniStat label="قيمة التعديلات الإدارية" value={fmtNum(stats.totalAdminAdjValue)} color="sky" icon={Wallet} />
        <MiniStat label="إجمالي الإضافي" value={`${(stats.otHours / 8).toFixed(2)} يوم`} color="emerald" icon={Clock} />
        <MiniStat label="أيام حضور" value={fmtInt(stats.presentDays)} color="emerald" icon={TrendingUp} />
      </div>

      <div className="grid lg:grid-cols-5 gap-5 items-start">
        {/* Employees table — يمين، 60% — أول في DOM عشان يظهر يمين في RTL */}
        <Card padding="md" className="lg:col-span-3 lg:!order-2 flex flex-col">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h3 className="font-bold text-sm flex items-center gap-2 shrink-0">
              <Wallet size={16} className="text-emerald-500" /> جدول الموظفين
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="ابحث..."
                  className="pr-9 !py-1.5 text-xs w-44"
                />
              </div>
              <div className="flex items-center gap-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
                {[
                  { id: 'net', label: 'الصافي', icon: Wallet },
                  { id: 'name', label: 'الاسم', icon: ArrowDownAZ },
                  { id: 'days', label: 'الأيام', icon: TrendingUp },
                  { id: 'late', label: 'التأخير', icon: Clock },
                ].map((s: any) => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setSortBy(s.id)}
                      className={`px-2 py-1 rounded-md text-[11px] font-bold flex items-center gap-1 transition-all ${
                        sortBy === s.id
                          ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow'
                          : 'text-slate-500'
                      }`}
                    >
                      <Icon size={11} /> {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[640px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="text-right text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-l from-sky-600 to-indigo-600">
                  <th className="px-2 py-2">ID</th>
                  <th className="px-2 py-2">الاسم</th>
                  <th className="px-2 py-2">الوظيفة</th>
                  <th className="px-2 py-2 text-center">أيام</th>
                  <th className="px-2 py-2 text-center">إضافي<br/>(يوم)</th>
                  <th className="px-2 py-2 text-center">خصم<br/>(يوم)</th>
                  <th className="px-2 py-2 text-center">الصافي</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d: any, i: number) => {
                  const otDays = (d['إجمالي ساعات الإضافي'] || 0) / 8;
                  const dedDays =
                    (d['أيام جزاء التأخير'] || 0) +
                    (d['أيام جزاء الخروج المبكر'] || 0) +
                    (d['أيام جزاء النسيان'] || 0);
                  return (
                    <tr key={d.ID} className="border-b border-slate-100 dark:border-slate-800 hover:bg-sky-50/30 dark:hover:bg-sky-900/10">
                      <td className="px-2 py-1.5 text-center text-slate-500 font-mono">{d.ID}</td>
                      <td className="px-2 py-1.5 font-bold truncate max-w-[160px]">{d.Name}</td>
                      <td className="px-2 py-1.5 text-slate-600 dark:text-slate-400 truncate max-w-[120px]">{d.Job}</td>
                      <td className="px-2 py-1.5 text-center font-bold text-indigo-600 dark:text-indigo-400">{d['أيام الحضور المستحقة']}</td>
                      <td className="px-2 py-1.5 text-center font-bold text-sky-600 dark:text-sky-400">
                        {otDays > 0 ? fmtNum(otDays) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-center font-mono text-rose-600 dark:text-rose-400 font-bold">
                        {dedDays > 0 ? fmtNum(dedDays) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-center font-mono font-bold text-emerald-600 dark:text-emerald-400">
                        {fmtNum(d['صافي القبض المستحق'])}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* By job — يسار، 40% — ثاني في DOM عشان يظهر يسار في RTL */}
        <Card padding="md" className="lg:col-span-2 lg:!order-1 flex flex-col">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h3 className="font-bold text-sm flex items-center gap-2 shrink-0">
              <BarChart3 size={16} className="text-indigo-500" /> توزيع حسب الوظيفة
            </h3>
          </div>
          <div className="overflow-x-auto max-h-[640px]">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-right text-[10px] font-bold uppercase tracking-wider text-slate-100 bg-gradient-to-l from-slate-700 to-slate-800">
                  <th className="px-2 py-2">الوظيفة</th>
                  <th className="px-2 py-2 text-center">عدد</th>
                  <th className="px-2 py-2 text-center">أيام</th>
                  <th className="px-2 py-2 text-center">إضافي<br/>(يوم)</th>
                  <th className="px-2 py-2">إجمالي الصافي</th>
                </tr>
              </thead>
              <tbody>
                {topJobs.map(([job, data]) => {
                  const otDays = (data.ot || 0) / 8;
                  return (
                    <tr key={job} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="px-2 py-1.5 font-bold">{job}</td>
                      <td className="px-2 py-1.5 text-center">{data.count}</td>
                      <td className="px-2 py-1.5 text-center">{data.days}</td>
                      <td className="px-2 py-1.5 text-center font-bold text-sky-600 dark:text-sky-400">
                        {otDays > 0 ? fmtNum(otDays) : '—'}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-emerald-600 dark:text-emerald-400 font-bold">
                        {fmtNum(data.net)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
  sub,
}: {
  icon: any;
  label: string;
  value: string;
  color: string;
  sub?: string;
}) {
  return (
    <div className={`rounded-2xl p-5 text-white bg-gradient-to-br ${color} shadow-lg`}>
      <div className="flex items-center gap-2 mb-2 opacity-90">
        <Icon size={16} />
        <span className="text-xs font-bold">{label}</span>
      </div>
      <div className="text-2xl font-black font-mono">{value}</div>
      {sub && <div className="text-[11px] opacity-80 mt-1">{sub}</div>}
    </div>
  );
}

function MiniStat({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: any;
  label: string;
  value: string;
  color: 'amber' | 'emerald' | 'sky';
}) {
  const colors = {
    amber: 'from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 text-amber-700 dark:text-amber-300',
    emerald: 'from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 text-emerald-700 dark:text-emerald-300',
    sky: 'from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 text-sky-700 dark:text-sky-300',
  };
  return (
    <div className={`rounded-2xl p-4 bg-gradient-to-br ${colors[color]} flex items-center gap-3`}>
      <Icon size={20} />
      <div>
        <div className="text-lg font-black font-mono">{value}</div>
        <div className="text-[11px] opacity-80">{label}</div>
      </div>
    </div>
  );
}
